import { computed, createModel, signal } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import type { AllowedInstrumentTypesSignal, InstrumentChangeEvent, InstrumentSignal } from 'src/types/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import type { EventBus } from 'src/types/EventBus/EventBus';
import { z } from 'zod';

const DEFAULT_ALLOWED_INSTRUMENT_TYPES: InstrumentType[] = ['equity'];

export const instrumentSelectionStateSchema = z.object({
    instrument: z.string().nullable().default(null),
});

export type InstrumentSelectionState = z.infer<typeof instrumentSelectionStateSchema>;

export type InstrumentSelectionModelEventBus = EventBus<
    InstrumentSignal & AllowedInstrumentTypesSignal,
    InstrumentChangeEvent
>;

function normalizeInstrument(instrument: string | null): string | null {
    return instrument === '' ? null : instrument;
}

interface InstrumentSelectionModelInit {
    persisted: InstrumentSelectionState;
    allowedInstrumentTypes?: InstrumentType[];
}

export interface InstrumentSelectionModelShape {
    readonly dirty: ReadonlySignal<boolean>;
    readonly eventBus: InstrumentSelectionModelEventBus;
    readonly instrument: ReadonlySignal<string | null>;
    setInstrumentFromLocalChange(next: string): boolean;
    setInstrumentFromLink(next: string): boolean;
    toJSON(): InstrumentSelectionState;
    markClean(): void;
}

export const InstrumentSelectionModel: ModelConstructor<InstrumentSelectionModelShape, [InstrumentSelectionModelInit]> =
    createModel<InstrumentSelectionModelShape, [InstrumentSelectionModelInit]>((init: InstrumentSelectionModelInit) => {
        const initialInstrument = normalizeInstrument(init.persisted.instrument);
        const persistedInstrument = signal<string | null>(initialInstrument);
        const cleanInstrument = signal<string | null>(initialInstrument);
        const allowedInstrumentTypes = signal<InstrumentType[]>(
            init.allowedInstrumentTypes ?? DEFAULT_ALLOWED_INSTRUMENT_TYPES,
        );

        const instrument: ReadonlySignal<string | null> = computed(() => persistedInstrument.value);
        const headerInstrument: ReadonlySignal<string> = computed(() => persistedInstrument.value ?? '');
        const allowedInstrumentTypesReadonly: ReadonlySignal<InstrumentType[]> = computed(
            () => allowedInstrumentTypes.value,
        );
        const dirty: ReadonlySignal<boolean> = computed(() => persistedInstrument.value !== cleanInstrument.value);

        const setInstrument = (next: string): boolean => {
            const normalized = normalizeInstrument(next);
            if (persistedInstrument.value === normalized) {
                return false;
            }

            persistedInstrument.value = normalized;
            return true;
        };

        const eventBus: InstrumentSelectionModelEventBus = EventBusBuilder.create()
            .addSignal('widget::instrument', () => headerInstrument)
            .addSignal('widget::allowedInstrumentTypes', () => allowedInstrumentTypesReadonly)
            .addEvent('widget::instrumentChange', ({ instrument }) => {
                setInstrument(instrument);
            })
            .build();

        return {
            dirty,
            eventBus,
            instrument,
            setInstrumentFromLocalChange: setInstrument,
            setInstrumentFromLink: setInstrument,
            toJSON(): InstrumentSelectionState {
                return { instrument: persistedInstrument.value };
            },
            markClean(): void {
                cleanInstrument.value = persistedInstrument.value;
            },
        };
    });

export type InstrumentSelectionModel = Model<InstrumentSelectionModelShape>;
